import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Input, Table, message } from "antd";
import {
  EyeOutlined,
  ReloadOutlined,
  TeamOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import { apiClient } from "../../../shared/api/AllApi";
import { formatCurrency, toApiNumber } from "../../../utils/formatters";
import { fetchSystemConfig, readCachedSystemConfig } from "../../../shared/lib/systemConfig";
import { ListPage, StatCard, T } from "../../../shared/ui/PageShell";
import { vendorFinanceFromFarmerRow } from "../lib/vendorFinance";
import { fixedStart } from "../../../i18";
import useTablePagination from "../../../shared/ui/useTablePagination";

export default function FarmerPayments({ embedded = false } = {}) {
  const { t, i18n } = useTranslation();
  const { pagination } = useTablePagination();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [config, setConfig] = useState(() => readCachedSystemConfig());
  const currency = config?.currency_code || "IQD";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, response] = await Promise.all([
        fetchSystemConfig(),
        apiClient.get("/farmers-finance"),
      ]);
      setConfig(cfg);
      const farmers = Array.isArray(response.data?.farmers) ? response.data.farmers : [];
      setRows(
        farmers
          .map(vendorFinanceFromFarmerRow)
          .filter((farmer) => toApiNumber(farmer.remaining, 0) > 0.0001)
      );
    } catch (error) {
      setRows([]);
      message.error(error?.response?.data?.message || t("loadError", "Failed to load"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) =>
      String(row.name || "").toLowerCase().includes(term)
      || String(row.phone || "").toLowerCase().includes(term)
    );
  }, [rows, search]);

  const outstandingTotal = rows.reduce(
    (sum, row) => sum + toApiNumber(row.remaining, 0),
    0
  );

  const columns = [
    {
      title: <b>{t("actions", "Actions")}</b>,
      key: "actions",
      width: 180,
      align: "center",
      fixed: fixedStart(i18n.language),
      render: (_, row) => (
        <Link to={`/farmer-settlements?vendor=${row.vendor_id}`}>
          <Button type="primary" size="small" icon={<EyeOutlined />}>
            {t("reviewAndSettle", "Review & settle")}
          </Button>
        </Link>
      ),
    },
    {
      title: <b>{t("vendorsColName", "Farmer")}</b>,
      dataIndex: "name",
      key: "name",
      render: (name, row) => (
        <Link to={`/farmer-settlements?vendor=${row.vendor_id}`} style={{ fontWeight: 700 }}>
          {name}
        </Link>
      ),
    },
    {
      title: <b>{t("phone", "Phone")}</b>,
      dataIndex: "phone",
      key: "phone",
      render: (value) => value || "—",
    },
    {
      title: <b>{t("vendorGross", "Gross")}</b>,
      dataIndex: "gross",
      key: "gross",
      align: "right",
      render: (value) => formatCurrency(value, currency),
    },
    {
      title: <b>{t("batchExpenses", "Charges")}</b>,
      dataIndex: "expenses",
      key: "expenses",
      align: "right",
      render: (value) => <span style={{ color: T.red }}>{formatCurrency(value, currency)}</span>,
    },
    {
      title: <b>{t("vendorAlreadyPaid", "Already paid")}</b>,
      dataIndex: "paid",
      key: "paid",
      align: "right",
      render: (value) => <span style={{ color: T.green }}>{formatCurrency(value, currency)}</span>,
    },
    {
      title: <b>{t("vendorRemainingToClear", "Still to pay")}</b>,
      dataIndex: "remaining",
      key: "remaining",
      align: "right",
      render: (value) => <b style={{ color: T.red }}>{formatCurrency(value, currency)}</b>,
    },
  ];

  return (
    <>
      <ListPage
        compact={embedded}
        breadcrumb={embedded ? undefined : [t("vendorsMenu", "Farmers"), t("farmerAccountsPay", "Pay farmers")]}
        title={t("farmerAccountsPay", "Pay farmers")}
        subtitle={t(
          "farmerPaymentsHint",
          "Only farmers with an unpaid balance are shown. Review the sales, products, dates and charges before settling."
        )}
        stats={[
          <StatCard
            key="farmers"
            icon={<TeamOutlined style={{ color: T.primary }} />}
            label={t("farmersAwaitingPayment", "Farmers awaiting payment")}
            value={rows.length}
            iconBg="#dbeafe"
          />,
          <StatCard
            key="outstanding"
            icon={<WalletOutlined style={{ color: T.red }} />}
            label={t("vendorRemainingToClear", "Still to pay")}
            value={formatCurrency(outstandingTotal, currency)}
            iconBg="#fee2e2"
            valueColor={T.red}
          />,
        ]}
        toolbar={
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <Input.Search
              allowClear
              value={search}
              placeholder={t("searchHere", "Search here")}
              onChange={(event) => setSearch(event.target.value)}
              style={{ width: 280 }}
            />
            <div style={{ flex: 1 }} />
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
              {t("currencyTotalsRefresh", "Refresh")}
            </Button>
          </div>
        }
      >
        <Table
          loading={loading}
          columns={columns}
          dataSource={filtered.map((row) => ({ ...row, key: row.vendor_id }))}
          pagination={pagination}
          scroll={{ x: 900 }}
          locale={{ emptyText: t("noFarmersAwaitingPayment", "No farmers are awaiting payment") }}
        />
      </ListPage>
    </>
  );
}
